import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

/**
 * NotFoundPage — 404 UX for public unknown routes.
 *
 * The Kubernetes ingress in front of this SPA serves `index.html` for all
 * non-/api paths with HTTP 200. To get a real HTTP 404 status the user needs
 * one of:
 *   1. A prerender service (prerender.io, react-snap, Cloudflare Snap) that
 *      honors `<meta name="prerender-status-code" content="404">` below.
 *   2. A static host (Netlify / Vercel / Cloudflare Pages) that honors
 *      `_redirects` / `vercel.json` / `404.html` (all shipped in `public/`).
 *   3. Emergent hosting-layer edge config — track via support@emergent.sh.
 *
 * The parent `RouteSEO` in `App.js` already applies
 * `noindex, nofollow, noarchive` for any pathname not in the public
 * whitelist, so this file only needs to override the title and emit the
 * prerender status hint.
 */
export default function NotFoundPage() {
  return (
    <>
      <Helmet>
        <title>Page not found — Kewl Event Solutions</title>
        {/* Signal to prerender services (prerender.io, react-snap, etc.)
            that this render should be served with an HTTP 404 status.
            No-op on the current ingress; harmless everywhere else. */}
        <meta name="prerender-status-code" content="404" />
      </Helmet>
      <div
        className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-6"
        data-testid="not-found-page"
      >
        <div className="max-w-md text-center">
          <p className="text-[#A89685] text-sm uppercase tracking-widest mb-4">
            Error 404
          </p>
          <h1 className="font-heading text-5xl sm:text-6xl text-[#4A3C31] leading-tight mb-6">
            Page not found
          </h1>
          <p className="text-[#5C554E] leading-relaxed mb-10">
            The page you are looking for doesn&apos;t exist or has been moved.
          </p>
          <Link
            to="/"
            className="inline-block rounded-full bg-[#4A3C31] hover:bg-[#5C554E] text-[#FDFBF7] px-8 py-4 text-sm font-medium transition-all"
            data-testid="not-found-home-link"
          >
            Return home
          </Link>
        </div>
      </div>
    </>
  );
}
